import type { BooAccessEvent, BooPerson, BooRole } from "@/lib/api";
// Relative and with its extension so node --test can load this file.
import { groupLabel, LEVEL_LABEL } from "../../lib/people-levels.ts";

export const ROLE_LABEL: Record<BooRole, string> = {
  owner: "Owner",
  admin: "Admin",
  member: "Standard",
};

export const ROLE_HINT: Record<BooRole, string> = {
  owner: "Can do everything, including choosing admins and owners.",
  admin: "Can change settings, apps, secrets, and channels, and add people.",
  member: "Can open the Boo and see how it's set up.",
};

export function personName(p: BooPerson | null | undefined, fallback = "Someone"): string {
  return p?.name || p?.email || fallback;
}

const PLATFORM: Record<string, string> = {
  slack: "Slack",
  teams: "Teams",
  gchat: "Google Chat",
  discord: "Discord",
};

function isBooRole(role: unknown): role is BooRole {
  return role === "owner" || role === "admin" || role === "member";
}

function levelWord(role: string | null): string {
  return isBooRole(role) ? ROLE_LABEL[role] : "another level";
}

function featureLevelWord(level: unknown): string {
  return level === "auto" || level === "confirm" || level === "disabled" ? LEVEL_LABEL[level] : "Off";
}

function detailName(e: BooAccessEvent): string {
  const name = e.details?.name;
  return typeof name === "string" && name ? name : personName(e.target, "someone");
}

type RoleChange = { role?: unknown; feature?: unknown; featureTitle?: unknown; from?: unknown; to?: unknown };

function rolesChangedText(e: BooAccessEvent, actor: string, featureTitle: (id: string) => string): string {
  const d = e.details ?? {};
  const parts: string[] = [];
  for (const name of Array.isArray(d.added) ? d.added : []) parts.push(`added the ${String(name)} group`);
  for (const name of Array.isArray(d.removed) ? d.removed : []) parts.push(`deleted the ${String(name)} group`);
  const added = new Set(Array.isArray(d.added) ? d.added.map(String) : []);
  const removed = new Set(Array.isArray(d.removed) ? d.removed.map(String) : []);
  const changes = (Array.isArray(d.changes) ? (d.changes as RoleChange[]) : []).filter(
    (c) => !added.has(String(c.role)) && !removed.has(String(c.role)),
  );
  for (const c of changes) {
    const title = featureTitle(String(c.feature)) || (typeof c.featureTitle === "string" ? c.featureTitle : String(c.feature));
    parts.push(`set ${title} for ${groupLabel(String(c.role))}: ${featureLevelWord(c.from)} → ${featureLevelWord(c.to)}`);
  }
  if (parts.length === 0) return `${actor} changed what people can use.`;
  return `${actor} ${parts.join("; ")}.`;
}

// One plain sentence per access-history entry.
// `booName` names the Boo where the Boo's own history would say "this Boo".
export function describeAccessEvent(
  e: BooAccessEvent,
  booName?: string | null,
  featureTitle: (id: string) => string = (id) => id.replace(/_/g, " "),
): string {
  const thisBoo = booName ?? "this Boo";
  const actor = personName(e.actor);
  const target = personName(e.target);
  const platform = PLATFORM[String(e.details?.platform ?? "")] ?? "a chat workspace";
  const source = String(e.details?.sourceName ?? "another Boo");
  const consumer = String(e.details?.consumerName ?? "a Boo");
  switch (e.kind) {
    case "role_granted":
      return `${actor} added ${target} as ${levelWord(e.role)}.`;
    case "role_changed":
      return `${actor} changed ${target} from ${levelWord(e.previousRole)} to ${levelWord(e.role)}.`;
    case "role_removed":
      return e.actor?.userId === e.target?.userId ? `${target} left.` : `${actor} removed ${target}.`;
    case "ownership_taken":
      return `${actor} took ownership for the organization.`;
    case "owner_assigned":
      return `${actor} made ${target} an owner for the organization.`;
    case "deleted_by_org_owner":
      return `${actor} deleted ${thisBoo}.`;
    case "workspace_attached":
      return `${actor} connected ${platform} to ${thisBoo}.`;
    case "workspace_released":
      return `${actor} released ${platform} from ${thisBoo}.`;
    case "link_requested":
      return `${actor} asked for ${consumer} to learn from ${source}.`;
    case "link_approved": {
      if (e.details?.direct) return `${actor} set up ${consumer} to learn from ${source}${onBehalf(e)}.`;
      if (e.details?.via === "auto") return `${actor} set up ${consumer} to learn from ${source}.`;
      const partly = hasAny(e.details?.denied) ? "part of " : "";
      return `${actor} approved ${partly}the request for ${consumer} to learn from ${source}${onBehalf(e)}.`;
    }
    case "link_declined":
      return `${actor} declined the request for ${consumer} to learn from ${source}${onBehalf(e)}.`;
    case "link_revoked":
      return e.details?.previousStatus === "requested"
        ? `${actor} withdrew the request for ${consumer} to learn from ${source}.`
        : `${actor} stopped ${consumer} learning from ${source}${onBehalf(e)}.`;
    case "link_more_requested":
      return `${actor} asked for ${consumer} to learn more from ${source}.`;
    case "link_more_approved":
      return `${actor} approved ${hasAny(e.details?.denied) ? "part of " : ""}the request for more${onBehalf(e)}: ${consumer} now learns more from ${source}.`;
    case "link_more_declined":
      return e.details?.withdrawn
        ? `${actor} withdrew the request for ${consumer} to learn more from ${source}.`
        : `${actor} declined the request for ${consumer} to learn more from ${source}${onBehalf(e)}.`;
    case "link_grant_changed":
      return `${actor} changed what ${source} shares with ${consumer}${onBehalf(e)}.`;
    case "link_admins_asked":
      return e.details?.sourceName
        ? `${actor} asked this Boo's admins to let it learn from ${String(e.details.sourceName)}.`
        : `${actor} asked this Boo's admins to let it learn from another Boo.`;
    case "link_expired":
      return e.details?.more
        ? `The request for ${consumer} to learn more from ${source} expired unanswered.`
        : `The request for ${consumer} to learn from ${source} expired unanswered.`;
    case "link_removal_confirmed":
      return `${consumer} stopped using what ${source} no longer shares.`;
    case "boo_handed_over": {
      const renamed = e.details?.renamedFrom ? ` and renamed it from ${String(e.details.renamedFrom)}` : "";
      return `${actor} handed ${thisBoo} over to ${target}${renamed}${onBehalf(e)}.`;
    }
    case "super_admin_access":
      return `Boo Lab support (${personName(e.actor, "a support engineer")}) opened ${thisBoo}.`;
    case "roles_changed":
      return rolesChangedText(e, actor, featureTitle);
    case "person_blocked":
      return `${actor} blocked ${detailName(e)}.`;
    case "person_unblocked":
      return `${actor} unblocked ${detailName(e)}.`;
    case "view_all_spend_granted":
      return `${actor} let ${detailName(e)} see everyone's usage costs.`;
    case "view_all_spend_revoked":
      return `${actor} stopped ${detailName(e)} seeing everyone's usage costs.`;
    case "group_added":
      return `${actor} added ${detailName(e)} to ${String(e.details?.group ?? "a group")}.`;
    case "group_removed":
      return `${actor} removed ${detailName(e)} from ${String(e.details?.group ?? "a group")}.`;
    case "groups_kept": {
      const groups = Array.isArray(e.details?.groups) ? e.details.groups.map(String).join(", ") : "their groups";
      return `${personName(e.target, "Someone")} kept ${groups} from before.`;
    }
    case "invite_access_removed":
      return `${actor} took back the invite to ${String(e.details?.email ?? "someone")} for ${thisBoo}.`;
    case "handle_linked": {
      const account = `${target}'s ${PLATFORM[String(e.details?.provider ?? "")] ?? "chat"} account`;
      if (e.details?.source === "install") return `${account} was linked when they installed Boo.`;
      if (e.details?.source === "backfill") return `${account} was linked automatically.`;
      return `${actor} linked ${accountWord(e)} to ${target}.`;
    }
    case "handle_unlinked":
      return `${actor} separated ${accountWord(e)} from ${target}.`;
    case "memories_archived":
      return `${actor} archived memories from private conversations.`;
    default:
      return `${actor} made a change.`;
  }
}

function accountWord(e: BooAccessEvent): string {
  const name = PLATFORM[String(e.details?.provider ?? "")];
  return name ? `a ${name} account` : "a chat account";
}

function onBehalf(e: BooAccessEvent): string {
  return e.details?.via === "org_owner" ? ", for the organization" : "";
}

// A scopes object with anything in it.
function hasAny(v: unknown): boolean {
  if (!v || typeof v !== "object") return false;
  const s = v as { skills?: { all?: boolean; only?: unknown[] }; memories?: { org?: boolean; channels?: { all?: boolean; only?: unknown[] }; people?: { all?: boolean; only?: unknown[] } } };
  const some = (c?: { all?: boolean; only?: unknown[] }) => !!c && (c.all === true || (c.only?.length ?? 0) > 0);
  return some(s.skills) || !!s.memories?.org || some(s.memories?.channels) || some(s.memories?.people);
}

export function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
